import {Group} from '@mantine/core';
import React from 'react';

import {hasUnsavedChanges} from 'notebook/entries/BaseEntryUtil';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import postTemplateReviewActionServerUpdate from 'notebook/reviews/postTemplateReviewActionServerUpdate';
import ReviewBadge from 'notebook/reviews/ReviewBadge';
import {useReviewController} from 'notebook/reviews/useReviewController';
import TemplatePublishingButton, {
  TemplatePublishingButtonProps,
} from 'template-collectibles/publishing-ui/TemplatePublishingButton';
import {PUBLISH_REQUIRES_APPROVAL_TOOLTIP} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import {CenteredSpinner} from 'ui-library/Spinner';

export interface TemplateNoteTitleBadgesProps {
  templateId: string;
  openVersionApiId: string | undefined;
  isLatest: boolean;
  isViewingPublishedVersion: boolean;
  publishingButtonProps: TemplatePublishingButtonProps;
}

function getTemplateSendForReviewValidationError(templateId: string): string | null {
  if (!TemplateStore.inStore(templateId)) {
    return 'Template could not be found';
  }
  if (hasUnsavedChanges(templateId)) {
    return 'Template has unsaved changes';
  }
  return null;
}

export default function TemplateNoteTitleBadges({
  templateId,
  openVersionApiId,
  isLatest,
  isViewingPublishedVersion,
  publishingButtonProps,
}: TemplateNoteTitleBadgesProps): JSX.Element {
  const isCurrentEditableDraft = isLatest && !isViewingPublishedVersion;
  const controller = useReviewController(templateId, {
    enableReviewLifecycle: true,
    onReviewChange: postTemplateReviewActionServerUpdate,
    openVersionId: openVersionApiId || undefined,
    isCurrentEditableDraft,
    getSendForReviewValidationError: getTemplateSendForReviewValidationError,
    cyclicalPublishGating: {
      isLatest,
      isPublishOptionDisabledFromParent: publishingButtonProps.isPublishOptionDisabled,
    },
  });

  const publishState = controller.publishState;
  const showReviewBadge =
    openVersionApiId &&
    publishState != null &&
    !publishState.loading &&
    !publishState.error &&
    !publishState.inaccessible &&
    publishState.reviewRequired;

  return (
    <Group spacing={8} align="center" display="inline-flex">
      {publishState?.loading && <CenteredSpinner />}
      {showReviewBadge && <ReviewBadge reviewableId={templateId} controller={controller} />}
      <TemplatePublishingButton
        {...publishingButtonProps}
        isPublishOptionDisabled={
          publishState?.isPublishOptionDisabled ?? publishingButtonProps.isPublishOptionDisabled
        }
        publishDisabledTooltip={
          publishState?.isPublishBlockedByReview ? PUBLISH_REQUIRES_APPROVAL_TOOLTIP : undefined
        }
      />
    </Group>
  );
}
